<template>
  <div :class="wrapperClasses">
    <!-- Mascot on the left (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'left'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="computedMascotIds"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>

    <section :class="bannerClasses">
      <!-- Media section - order changes based on mascot position -->
      <div v-if="variant !== 'sidebar'" :class="mediaClasses">
        <client-only v-if="computedImages.length > 1">
          <Carousel :items="computedImages" class="absolute inset-0 h-full">
            <template #default="{ item: image }">
              <div class="relative h-full w-full">
                <img
                  :src="image.src"
                  :srcset="cloudinarySrcset(image.src, CTA_WIDTHS)"
                  :sizes="CTA_SIZES"
                  :alt="image.alt || ''"
                  :class="['z-10 h-full w-full', getImageClass(image.fit || 'cover'), image.class]"
                />
              </div>
            </template>
            <template #controls="{ goTo, current, count }">
              <CarouselDots :count="count" :active="current" @select="(i: number) => goTo(i)" />
            </template>
          </Carousel>
        </client-only>
        <img
          v-else-if="computedImages[0]"
          class="absolute inset-0 h-full w-full"
          :src="computedImages[0].src"
          :srcset="cloudinarySrcset(computedImages[0].src, CTA_WIDTHS)"
          :sizes="CTA_SIZES"
          :alt="computedImages[0].alt || ''"
          :class="[
            'h-full w-full',
            getImageClass(computedImages[0].fit || 'cover'),
            computedImages[0].class,
          ]"
        />
      </div>
      <!-- Info section - order changes based on mascot position -->
      <div :class="infoClasses">
        <div :class="infoTextClasses">
          <h2 v-if="computedTitle" :class="titleClasses">
            {{ computedTitle }}
          </h2>
          <p
            v-if="computedDescription"
            :class="descriptionClasses"
            v-html="computedDescription"
          ></p>
          <app-button
            v-if="computedCtaButton"
            :class="actionClasses"
            :as="computedCtaButton.external ? 'a' : 'nuxt-link'"
            :to="computedCtaButton.external ? undefined : computedCtaButton.url"
            :href="computedCtaButton.external ? computedCtaButton.url : undefined"
            :variant="computedCtaButton.variant || 'pink'"
            arrow-type="double"
            position="right"
            size="desktop"
          >
            {{ computedCtaButton.text }}
          </app-button>
        </div>
      </div>
    </section>

    <!-- Mascot on the right (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'right'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="computedMascotIds"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Carousel from 'refresh-ui/src/components/molecules/Carousel.vue'
import CarouselDots from 'refresh-ui/src/components/atoms/CarouselDots.vue'
import type { CtaBanner as CtaBannerType } from '@/types/contentful'
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'
import { useMascots } from '@/composables/useMascots'
import { cloudinarySrcset } from '@/utils/cloudinary'

// The media column is basis-2/5 from Tailwind's sm (768px), but tall beside the text until 1280px.
const CTA_WIDTHS = [400, 600, 800, 1200]
const CTA_SIZES = '(min-width: 1280px) 40vw, (min-width: 768px) 60vw, 100vw'

/**
 * @interface CtaImage
 * @property {string} src - The source URL of the image.
 * @property {string} [alt] - The alternative text for the image.
 * @property {'contain' | 'cover' | 'fill'} [fit] - The object-fit CSS property for the image. Defaults to 'cover'.
 */
export interface CtaImage {
  src: string
  alt?: string
  fit?: 'contain' | 'cover' | 'fill'
  class?: string
}

export interface CtaButton {
  text: string
  url: string
  variant?:
    | 'peach'
    | 'pink'
    | 'green'
    | 'purple'
    | 'blue'
    | 'DEFAULT'
    | 'rosa'
    | 'lila'
    | 'ljusrosa'
    | 'ljuslila'
  external?: boolean
}

interface Props {
  // Contentful CTA data
  ctaData?: CtaBannerType
  // Fallback props for backward compatibility
  images?: CtaImage[]
  title?: string
  description?: string
  ctaButton?: CtaButton
  // Styling props
  theme?: 'membership' | 'app' | 'default'
  mascotPosition?: 'left' | 'right'
  variant?: 'default' | 'sidebar' | 'compact'
  // Mascot props
  mascotIds?: string[]
  mascotVariant?: 'happy' | 'neutral' | 'worried'
}

const props = withDefaults(defineProps<Props>(), {
  ctaData: undefined,
  images: () => [],
  title: '',
  description: '',
  ctaButton: undefined,
  theme: 'default',
  mascotPosition: 'right',
  variant: 'default',
  mascotIds: () => [],
  mascotVariant: 'neutral',
})

// Mascot functionality
const { getMascots, getMascotConfig } = useMascots()

// Get mascots from Contentful data or props
const computedMascotIds = computed((): string[] => {
  if (props.ctaData?.fields?.mascot) {
    return props.ctaData.fields.mascot
  }
  return props.mascotIds || []
})

// Get mascot position from Contentful data or props
const computedMascotPosition = computed((): 'left' | 'right' => {
  if (props.ctaData?.fields?.mascotPlacement?.[0]) {
    // Map Swedish Contentful values to our component values
    return props.ctaData.fields.mascotPlacement[0] === 'Vänster' ? 'left' : 'right'
  }
  return props.mascotPosition || 'right'
})

// Get mascots with validation
const mascots = computed(() => {
  return getMascots(computedMascotIds.value)
})

// Get mascot configuration for CTA
const mascotConfig = computed(() => {
  return getMascotConfig('cta')
})

// Determine if we should show mascots
const shouldShowMascots = computed(() => {
  return mascots.value.length > 0
})

// Use Contentful data if available, otherwise fall back to props
const computedImages = computed((): CtaImage[] => {
  if (props.ctaData?.fields?.cloudinaryGallery) {
    return props.ctaData.fields.cloudinaryGallery.map((image: any) => ({
      src: fixImageUrlProtocol(image.url),
      alt: image.context?.custom?.alt || '',
      fit: getImageFitForTheme(props.theme, props.ctaData?.fields?.imageOtherAspect ?? false),
    }))
  }
  return props.images
})

// Helper function to determine image fit based on theme and imageOtherAspect
const getImageFitForTheme = (
  theme: string,
  imageOtherAspect: boolean = false,
): 'contain' | 'cover' | 'fill' => {
  // If imageOtherAspect is true, use contain (for transparent PNG images)
  // If imageOtherAspect is false, use cover (for regular images)
  if (imageOtherAspect) {
    return 'contain'
  }

  switch (theme) {
    case 'membership':
      return 'contain'
    case 'app':
      return 'fill'
    case 'newsletter':
      return 'cover'
    case 'shop':
      return 'cover'
    default:
      return 'cover' // Default to cover for regular images
  }
}

// Helper function to get CSS classes for image fit
const getImageClass = (fit: 'contain' | 'cover' | 'fill'): string => {
  switch (fit) {
    case 'contain':
      return 'object-contain py-8'
    case 'cover':
      return 'object-cover'
    case 'fill':
      return 'object-fill'
    default:
      return 'object-cover'
  }
}

const computedTitle = computed((): string => {
  return props.ctaData?.fields?.title || props.title || ''
})

const computedDescription = computed((): string => {
  // Extract text from rich text body
  if (props.ctaData?.fields?.body?.content) {
    return props.ctaData.fields.body.content
      .map((paragraph: any) => paragraph.content?.map((text: any) => text.value).join('') || '')
      .join('<br/>')
  }
  return props.description || ''
})

const computedCtaButton = computed((): CtaButton | null => {
  if (props.ctaData?.fields?.urlText && props.ctaData.fields.url) {
    return {
      text: props.ctaData.fields.urlText,
      url: props.ctaData.fields.url,
      variant:
        props.variant === 'sidebar'
          ? 'lila'
          : mapUrlColorToVariant(props.ctaData.fields.urlColor?.[0] || ''),
      external: props.ctaData.fields.url.startsWith('http'),
    }
  }
  return props.ctaButton || null
})

// Helper function to map Contentful color names to our variants
const mapUrlColorToVariant = (
  color: string,
):
  | 'peach'
  | 'pink'
  | 'green'
  | 'purple'
  | 'blue'
  | 'DEFAULT'
  | 'rosa'
  | 'lila'
  | 'ljusrosa'
  | 'ljuslila' => {
  switch (color) {
    case 'Rosa':
      return 'rosa'
    case 'Lila':
      return 'lila'
    default:
      return 'rosa' // Default to new pink CTA
  }
}

const bannerClasses = computed((): string => {
  const baseClasses = 'flex flex-col overflow-hidden w-full'

  switch (props.variant) {
    case 'sidebar': {
      // Sidebar: border radius based on mascot placement
      let sidebarRadiusClasses = ''
      if (shouldShowMascots.value) {
        if (computedMascotPosition.value === 'left') {
          sidebarRadiusClasses = 'rounded-[20px] rounded-tl-[0px] mt-4'
        } else {
          sidebarRadiusClasses = 'rounded-[20px] rounded-br-[0px] mb-4'
        }
      } else {
        sidebarRadiusClasses = 'rounded-[20px]'
      }
      return `${baseClasses} ${sidebarRadiusClasses} bg-white`
    }
    case 'compact':
      // Compact: always full rounded corners
      return `${baseClasses} rounded-[20px] bg-purple-lightest`
    default: {
      // Default: border radius based on mascot placement
      let defaultRadiusClasses = ''
      if (shouldShowMascots.value) {
        if (computedMascotPosition.value === 'left') {
          defaultRadiusClasses = 'rounded-[20px] rounded-tl-[0px]'
        } else {
          defaultRadiusClasses = 'rounded-[20px] rounded-br-[0px]'
        }
      } else {
        defaultRadiusClasses = 'rounded-[20px]'
      }
      return `${baseClasses} ${defaultRadiusClasses} bg-purple-lightest sm:flex-row ${shouldShowMascots.value ? 'flex-1' : ''}`
    }
  }
})

const mediaClasses = computed((): string => {
  const baseClasses = 'relative min-h-72 flex-1 basis-2/5 overflow-hidden pr-2'

  if (props.variant === 'sidebar') {
    return 'hidden' // Hide media for sidebar variant
  }

  // Add order class based on mascot position
  const orderClass =
    shouldShowMascots.value && computedMascotPosition.value === 'left' ? 'order-2' : 'order-1'

  if (props.variant === 'default') {
    return `${baseClasses} cta-banner-media sm:cta-banner-media ${orderClass}`
  }

  return `${baseClasses} ${orderClass}`
})

const infoClasses = computed((): string => {
  const baseClasses = 'flex-1 basis-3/5 flex flex-col justify-center items-start'

  // Add order class based on mascot position
  const orderClass =
    shouldShowMascots.value && computedMascotPosition.value === 'left' ? 'order-1' : 'order-2'

  switch (props.variant) {
    case 'sidebar':
      return `${baseClasses} text-black text-left ${orderClass}`
    case 'compact':
      return `${baseClasses} bg-white ${orderClass}`
    default:
      return `${baseClasses} bg-white text-center ${orderClass}`
  }
})

const infoTextClasses = computed((): string => {
  const basePadding = 'px-[23px] md:px-[70px] py-[21px] md:py-[74px] text-left'
  const sidebarPadding = 'px-[23px] py-[21px] text-left'

  switch (props.variant) {
    case 'compact':
      return `${basePadding} font-body`
    case 'sidebar':
      return `${sidebarPadding} font-body`
    default:
      return `${basePadding} font-body`
  }
})

const titleClasses = computed((): string => {
  const baseClasses = 'font-headline text-h2 font-normal text-black text-left'

  switch (props.variant) {
    case 'sidebar':
      return `${baseClasses} text-black mb-5`
    case 'compact':
      return `${baseClasses} text-h3`
    default:
      return `${baseClasses} text-h2`
  }
})

const descriptionClasses = computed((): string => {
  const baseClasses = 'font-body text-body tracking-normal text-slate-dark text-left'

  switch (props.variant) {
    case 'sidebar':
      return `${baseClasses} mb-5`
    case 'compact':
      return `${baseClasses} text-sm`
    default:
      return `${baseClasses} my-4 md:my-6`
  }
})

const actionClasses = computed((): string => {
  const baseClasses = 'w-full md:w-auto py-6'

  switch (props.variant) {
    case 'compact':
      return `${baseClasses} mt-2 py-2 px-4`
    default:
      return `${baseClasses} mt-2 py-1 px-6`
  }
})

// Mascot container classes with justify-content
const mascotContainerClasses = computed((): string => {
  const baseClasses = 'pointer-events-none flex flex-shrink-0 items-center'
  const justifyClass =
    computedMascotPosition.value === 'left' ? 'justify-start' : 'justify-end self-end'
  return `${baseClasses} ${justifyClass}`
})

// Wrapper classes for flexbox layout
const wrapperClasses = computed((): string => {
  if (shouldShowMascots.value && props.variant !== 'sidebar') {
    return 'flex items-start gap-4 sm:flex-row flex-col'
  }
  return ''
})
</script>
